// Leaderboards (docs/APP_REFERENCE.md "Leaderboard"): signed-in listeners ranked five ways, under their chat names,
// from the live worker's GET /account/board (worker/board.js): Listened, Stamps, Streak, City fans (the city on
// screen) and Chatty, each with its own two windows. The worker sends chat author keys, never ids, and the names
// people chose for the chat; the page names the rows as the chat does (site/js/live/chat.js: the chosen name with its
// ✓, else the random one), the signed-in visitor's own row marked. The Leaderboard panel (the
// trophy in the player) shows them (site/js/app.js renderBoard). Pure but for the fetch it's handed, so node can test it: node --test tests/
import { nameOf, namesOf, hueOf, isAuthor, nickOf } from './chat.js';
import { duration } from '../ui/passport.js';

export const BOARD_EVERY_MS = 60000;    // while the panel is open: the boards again this often

/** The boards, in the order of their chips, each with its two windows ([key, label]). */
export const BOARD_TYPES = [
  { id: 'listened', label: 'Listened', tabs: [['week', 'This week'], ['all', 'All time']] },
  { id: 'stamps', label: 'Stamps', tabs: [['week', 'This week'], ['all', 'All time']] },
  { id: 'streak', label: 'Streak', tabs: [['now', 'Current'], ['best', 'Best']] },
  { id: 'fans', label: 'Fans', tabs: [['week', 'This week'], ['all', 'All time']] },
  { id: 'chat', label: 'Chatty', tabs: [['week', 'This week'], ['all', 'All time']] },
];
export const boardType = id => BOARD_TYPES.find(t => t.id === id) ?? BOARD_TYPES[0];

/** The boards (GET /account/board, City fans for `city`, cookies included for `me`), or null when the worker can't be reached or says no. */
export async function fetchBoard(base, city, fetcher = (...a) => fetch(...a)) {
  try {
    const query = city ? `?city=${encodeURIComponent(city)}` : '';
    const res = await fetcher(`${base}/account/board${query}`, { credentials: 'include', signal: AbortSignal.timeout?.(10000) });
    const data = res.ok ? await res.json() : null;
    return data?.ok && data.boards && data.week ? data : null;
  } catch { return null; }
}

/**
 * One board's rows as the view shows them: { place, a, name, chosen, hue, v, x, me }. Names are the chat's: the one
 * its author chose (`n` on the worker's row; `chosen`, which the panel marks ✓ as the chat does), else the random one,
 * told apart as in the chat ("sleepy fox ·3f"). `me`, the visitor as the worker says ({ a, n }: its author key and
 * chosen name), marks its row; off the top (`place`, its place on that board: { r, v, x }), its row comes last,
 * after a gap when places lie between (`gap: true`).
 */
export function boardRows(board, me = null, place = null) {
  const rows = (board?.rows ?? []).filter(r => isAuthor(r.a) && Number.isFinite(r.v));
  const mine = isAuthor(me?.a) ? me.a : null;
  const off = !!mine && !!place && !rows.some(r => r.a === mine);
  const nicks = new Map([...rows, ...(off ? [me] : [])].filter(r => nickOf(r.n)).map(r => [r.a, r.n]));
  const names = namesOf([...rows.map(r => r.a), ...(off ? [mine] : [])], nicks);
  const view = (a, r, v, x) => ({ place: r, a, name: names.get(a) ?? nameOf(a), chosen: nicks.has(a), hue: hueOf(a), v, x: x ?? null, me: a === mine });
  const out = rows.map(r => view(r.a, r.r, r.v, r.x));
  if (off) out.push({ ...view(mine, place.r, place.v, place.x), gap: place.r > (rows.at(-1)?.r ?? 0) + 1 });
  return out;
}

const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * What a row shows for its board: `value` (right) and `extra` (muted, before it), each { text, stamp } (stamp: the
 * passport's stamp icon before it), or extra null. Listened: the time alone; Stamps: the stamps, and the
 * time that breaks a tie; Streak: the days (current: the best beside it); City fans: the time there; Chatty: messages.
 */
export function boardCells(type, tab, { v, x }) {
  if (type === 'listened') return { value: { text: duration(v) }, extra: null };
  if (type === 'stamps') return { value: { text: String(v), stamp: true }, extra: Number.isFinite(x) ? { text: duration(x) } : null };
  if (type === 'streak') return { value: { text: plural(v, 'day', 'days') }, extra: tab === 'now' && Number.isFinite(x) && x > v ? { text: `best ${x}` } : null };
  if (type === 'chat') return { value: { text: plural(v, 'msg', 'msgs') }, extra: null };
  return { value: { text: duration(v) }, extra: null };
}

/** When the week's boards start again, as their head says it: "new week in 3 d 4 h", "in 5 h 20 m", "in 12 min". */
export function resetsIn(ends, now) {
  const m = Math.max(1, Math.ceil((ends - now) / 60000));
  if (m < 60) return `new week in ${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `new week in ${h} h${m % 60 ? ` ${m % 60} m` : ''}` : `new week in ${Math.floor(h / 24)} d${h % 24 ? ` ${h % 24} h` : ''}`;
}

/** The line beside the windows: when the week starts again, what keeps a streak, or how many are on the board. */
export function boardInfo(type, tab, data, now) {
  if (!data) return '';
  if (tab === 'week') return resetsIn(data.week.ends, now);
  if (tab === 'now') return '5 min a day (UTC) keeps it';
  const people = data.boards[type]?.[tab]?.people ?? 0;
  return type === 'fans' ? plural(people, 'fan', 'fans') : `${plural(people, 'person', 'people')} on it`;
}

/** "1st", "2nd", "3rd", "4th" … "11th", "12th", "13th", "21st". */
export function ordinal(n) {
  const tens = n % 100, ones = n % 10;
  return `${n}${tens >= 11 && tens <= 13 ? 'th' : ones === 1 ? 'st' : ones === 2 ? 'nd' : ones === 3 ? 'rd' : 'th'}`;
}

/** How to get on a board, for a signed-in visitor not on it yet (`city`, the city on screen's name, for its fans). */
function howTo(type, tab, city) {
  const week = tab === 'week' ? ' this week' : '';
  if (type === 'stamps') return `Stamp a city${week} (5 min of music there) and you are on it.`;
  if (type === 'streak') return 'Listen 5 minutes today to start a streak.';
  if (type === 'fans') return `Listen a minute in ${city}${week} to be one of its fans.`;
  if (type === 'chat') return `Say something in the chat${week} and you are on it.`;
  return `Listen a minute${week} and you are on it.`;
}

/**
 * What the foot of the board says to the visitor: signed out, the way on; signed in, where they are or how to get
 * on. { text, action: 'sign-in' | 'hide' | 'show' }. `place` is theirs on this board and window ({ r }), `people`
 * how many are on it.
 */
export function boardNote({ signedIn, hidden, place, type, tab, people = 0, city = 'this city' }) {
  if (!signedIn) return { text: 'Sign in to get on the boards. Your stamps come along; what you listen to counts from then on.', action: 'sign-in' };
  if (hidden) return { text: 'You are hidden from the boards. Everything still counts.', action: 'show' };
  if (!place) return { text: howTo(type, tab, city), action: 'hide' };
  const of = `${ordinal(place.r)} of ${Math.max(people, place.r)}`;
  return { text: type === 'fans' ? `You are ${city}'s ${of} fans${tab === 'week' ? ' this week' : ''}.` : `You are ${of}${tab === 'week' ? ' this week' : ''}.`, action: 'hide' };
}
